/**
 * Visual Registry Integrity Report
 * MOVED from src/developer/diagnostics/ (Phase J-2)
 */
import { VISUAL_AID_FACTORY_SUPPORTED_TYPES } from '../../../visuals/components/VisualAidFactory'
import {
  VISUAL_REGISTRY,
  VISUAL_REGISTRY_IMPORT_NAMES,
} from '../../../visuals/registry/visualRegistry'
import { VISUAL_AID_TYPES } from '../../../visuals/types'
import visualGovernanceMap from '../../../governance/data/VISUAL_GOVERNANCE_MAP.json'
import visualAidSchemas from '../../../governance/data/VISUAL_AID_SCHEMAS.json'

interface VisualGovernanceImplementedTypesShape {
  implementedTypes?: { types?: string[] }
}

export interface VisualRegistryIntegrityReport {
  duplicateImportNames: string[]
  duplicateRegistryKeys: string[]
  missingRegistryKeysFromVisualAidType: string[]
  missingRegistryKeysFromFactoryTypes: string[]
  extraRegistryKeysOutsideVisualAidType: string[]
  missingGovernanceTypesFromRuntimeTypes: string[]
  extraGovernanceTypesOutsideRuntimeTypes: string[]
  governanceImplementedTypesCountMismatch: boolean
  hasIntegrityIssues: boolean
}

function uniqueDuplicates(values: string[]): string[] {
  const counts = new Map<string, number>()
  values.forEach((value) => counts.set(value, (counts.get(value) ?? 0) + 1))
  return Array.from(counts.entries())
    .filter(([, count]) => count > 1)
    .map(([value]) => value)
    .sort()
}

export function getVisualRegistryIntegrityReport(): VisualRegistryIntegrityReport {
  const registryKeys = Object.keys(VISUAL_REGISTRY).sort()
  const visualAidTypes = Array.from(VISUAL_AID_TYPES).sort()
  const factoryTypes = Array.from(VISUAL_AID_FACTORY_SUPPORTED_TYPES).sort()
  const runtimeCanonicalTypes = Array.from(
    new Set([...visualAidTypes, ...registryKeys]),
  ).sort()
  const governanceImplementedTypes = Array.from(
    new Set(
      (
        (visualGovernanceMap as VisualGovernanceImplementedTypesShape)
          .implementedTypes?.types ?? []
      ).map((type) => `${type}`),
    ),
  ).sort()
  const registrySet = new Set(registryKeys)
  const visualTypeSet = new Set(visualAidTypes)
  const runtimeCanonicalSet = new Set(runtimeCanonicalTypes)
  const governanceImplementedSet = new Set(governanceImplementedTypes)
  const missingRegistryKeysFromVisualAidType = visualAidTypes.filter(
    (type) => !registrySet.has(type),
  )
  const missingRegistryKeysFromFactoryTypes = factoryTypes.filter(
    (type) => !registrySet.has(type),
  )
  const extraRegistryKeysOutsideVisualAidType = registryKeys.filter(
    (type) => !visualTypeSet.has(type),
  )
  const missingGovernanceTypesFromRuntimeTypes = runtimeCanonicalTypes.filter(
    (type) => !governanceImplementedSet.has(type),
  )
  const extraGovernanceTypesOutsideRuntimeTypes =
    governanceImplementedTypes.filter((type) => !runtimeCanonicalSet.has(type))
  const governanceImplementedTypesCountMismatch =
    governanceImplementedTypes.length !== runtimeCanonicalTypes.length
  const importedNamesSet = new Set(VISUAL_REGISTRY_IMPORT_NAMES)
  const canonicalImportListHasDuplicates =
    importedNamesSet.size !== VISUAL_REGISTRY_IMPORT_NAMES.length
  const duplicateImportNames = canonicalImportListHasDuplicates
    ? uniqueDuplicates([...VISUAL_REGISTRY_IMPORT_NAMES])
    : []
  const duplicateRegistryKeys: string[] = []
  return {
    duplicateImportNames,
    duplicateRegistryKeys,
    missingRegistryKeysFromVisualAidType,
    missingRegistryKeysFromFactoryTypes,
    extraRegistryKeysOutsideVisualAidType,
    missingGovernanceTypesFromRuntimeTypes,
    extraGovernanceTypesOutsideRuntimeTypes,
    governanceImplementedTypesCountMismatch,
    hasIntegrityIssues:
      duplicateImportNames.length > 0 ||
      duplicateRegistryKeys.length > 0 ||
      missingRegistryKeysFromVisualAidType.length > 0 ||
      missingRegistryKeysFromFactoryTypes.length > 0 ||
      extraRegistryKeysOutsideVisualAidType.length > 0 ||
      missingGovernanceTypesFromRuntimeTypes.length > 0 ||
      extraGovernanceTypesOutsideRuntimeTypes.length > 0 ||
      governanceImplementedTypesCountMismatch ||
      canonicalImportListHasDuplicates,
  }
}

/**
 * Visual validation alignment report — compares registry types vs schema types.
 * Moved here from visualGovernanceValidator.ts (deprecated) so that
 * buildTaxonomyGovernanceReport.ts no longer needs to import from the deprecated file.
 */
export function getVisualValidationAlignmentReport(): {
  registryTypes: string[]
  schemaTypes: string[]
  missingSchemasForRegistryTypes: string[]
  orphanSchemaTypes: string[]
} {
  const registryTypes = Object.keys(VISUAL_REGISTRY).sort()
  const schemaDefs =
    (
      visualAidSchemas as {
        definitions?: Record<string, unknown>
      }
    ).definitions ?? {}
  const schemaTypes = Object.keys(schemaDefs)
    .filter((type) => type !== 'baseVisualAid')
    .sort()
  const schemaSet = new Set(schemaTypes)
  const registrySet = new Set(registryTypes)
  return {
    registryTypes,
    schemaTypes,
    missingSchemasForRegistryTypes: registryTypes.filter(
      (type) => !schemaSet.has(type),
    ),
    orphanSchemaTypes: schemaTypes.filter((type) => !registrySet.has(type)),
  }
}
